import React, { useEffect, useState } from "react";
import UserSideNav from "../../components/user/UserSideNav";
import UserHeader from "../../components/user/UserHeader";
import SmartDataTable from "../../components/shared/SmartDataTable";
import NumberBalls from "../../components/shared/NumberBalls";
import { CheckCircle2, Eye, FileClock, Loader2, Ticket } from "lucide-react";
import { ticketRequestsApi, ticketsApi } from "../../services/api";

const API_BASE = process.env.REACT_APP_API_URL?.replace("/api", "") || "http://localhost:5000";

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-";
}

function requestTone(status = "") {
  if (["paid", "assigned"].includes(status)) return "live";
  if (["rejected", "cancelled"].includes(status)) return "danger";
  return "warn";
}

export default function MyTickets() {
  const [tickets, setTickets] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.allSettled([
      ticketsApi.getMyTickets(),
      ticketRequestsApi.getMine(),
    ])
      .then(([ticketResult, requestResult]) => {
        if (ticketResult.status === "fulfilled") {
          setTickets(ticketResult.value.data.data || []);
        } else {
          setTickets([]);
          setError(ticketResult.reason?.response?.data?.message || "Failed to load tickets.");
        }

        if (requestResult.status === "fulfilled") {
          setRequests(requestResult.value.data.data || []);
        } else {
          setRequests([]);
          if (ticketResult.status === "rejected") {
            setError(requestResult.reason?.response?.data?.message || "Failed to load tickets.");
          }
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const ticketColumns = [
    { name: "Ticket", selector: (row) => row.ticketNumber || "", sortable: true, cell: (row) => <span className="table-code">{row.ticketNumber}</span> },
    { name: "Pool", selector: (row) => row.pool?.poolId || "", sortable: true, cell: (row) => <span>{row.pool?.poolId || "-"}<small className="table-muted">{row.pool?.name || ""}</small></span>, grow: 1.2 },
    { name: "Draw", selector: (row) => row.drawDate || "", sortable: true, cell: (row) => fmtDate(row.drawDate) },
    { name: "Request", selector: (row) => row.ticketRequest?.requestId || "", sortable: true, cell: (row) => row.ticketRequest?.requestId || "-" },
    { name: "Numbers", cell: (row) => <NumberBalls numbers={row.whiteBalls} power={row.powerball} />, grow: 1.6 },
    { name: "Status", selector: (row) => row.status || "", sortable: true, cell: (row) => <span className={`table-status ${row.status === "winner" ? "live" : row.status === "no_match" ? "danger" : "warn"}`}>{row.status}</span> },
    {
      name: "Proof",
      button: true,
      cell: (row) => row.imageUrl ? (
        <a href={`${API_BASE}${row.imageUrl}`} target="_blank" rel="noreferrer">
          <button><Eye size={14} /> View</button>
        </a>
      ) : (
        <button disabled style={{ opacity: 0.4 }}><Eye size={14} /> None</button>
      ),
      minWidth: "120px",
    },
  ];

  const requestColumns = [
    { name: "Request", selector: (row) => row.requestId || row._id || "", sortable: true, cell: (row) => <strong>{row.requestId || row._id?.slice(-6)}</strong> },
    { name: "Pool", selector: (row) => row.pool?.poolId || "", sortable: true, cell: (row) => <span>{row.pool?.poolId || "-"}<small className="table-muted">{row.pool?.name || ""}</small></span>, grow: 1.35 },
    { name: "Quantity", selector: (row) => row.quantity || 0, sortable: true },
    { name: "Assigned", selector: (row) => row.assignedTickets?.length || 0, sortable: true, cell: (row) => `${row.assignedTickets?.length || 0} / ${row.quantity}` },
    { name: "Status", selector: (row) => row.status || "", sortable: true, cell: (row) => <span className={`table-status ${requestTone(row.status)}`}>{row.status?.replaceAll("_", " ")}</span> },
    { name: "Created", selector: (row) => row.createdAt || "", sortable: true, cell: (row) => fmtDate(row.createdAt) },
  ];

  return (
    <div className="user-shell">
      <UserSideNav />
      <div className="user-app">
        <UserHeader />
        <main className="user-main">
          <div className="welcome-block">
            <div>
              <div className="page-kicker">My Tickets</div>
              <h1>Assigned Tickets</h1>
              <p>Track purchased Powerball tickets assigned to you and request status for tickets still being processed by admin.</p>
            </div>
          </div>

          {error && <div className="auth-error">{error}</div>}

          {loading ? (
            <div className="page-loading"><Loader2 size={28} className="spin" /><span>Loading tickets...</span></div>
          ) : tickets.length === 0 && requests.length === 0 ? (
            <div className="empty-state"><Ticket size={36} /><p>No ticket requests or assigned tickets yet.</p></div>
          ) : (
            <>
              <section className="section-block">
                <div className="section-heading">
                  <h2><CheckCircle2 size={20} /> Purchased Tickets</h2>
                  <span className="muted-count">{tickets.length} tickets</span>
                </div>
                <SmartDataTable
                  columns={ticketColumns}
                  data={tickets}
                  loading={false}
                  searchFields={["ticketNumber", "pool.poolId", "pool.name", "ticketRequest.requestId", "status"]}
                  searchPlaceholder="Search tickets by number, pool, request, status..."
                  emptyMessage="No assigned tickets yet. Paid requests will appear after admin purchase."
                  paginationPerPage={25}
                />
              </section>

              <section className="section-block">
                <div className="section-heading">
                  <h2><FileClock size={20} /> Request Queue</h2>
                  <span className="muted-count">{requests.length} requests</span>
                </div>
                <SmartDataTable
                  columns={requestColumns}
                  data={requests}
                  loading={false}
                  searchFields={["requestId", "pool.poolId", "pool.name", "status", "createdAt"]}
                  searchPlaceholder="Search requests by pool, status, request ID..."
                  emptyMessage="No pending ticket requests."
                  paginationPerPage={25}
                />
              </section>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
